import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { translate } from '@/services/TranslationService';
import NOVOLoader from '../../shared/components/NOVOLoader';
import { changeAndPostProfile } from '../profile/actions';
import { getFallbackRedirect, getRedirectForAction } from './actionHandlers';
import { setDeeplinkData } from './actions';
import { FALLBACK_REDIRECT_TIMEOUT_MS } from './constants';

const RouteHandler = ({ location, setHeaderTitle }) => {
  const dispatch = useDispatch();

  const isAuthenticated = useSelector(
    (state) => !!state.authReducer.auth_token
  );
  const [isProfileSyncing, setIsProfileSyncing] = useState(false);
  const [hasProcessedAuthParams, setHasProcessedAuthParams] = useState(false);
  const [persistedDeeplinkKey, setPersistedDeeplinkKey] = useState('');

  const queryParams = useMemo(() => {
    const params = new URLSearchParams(location.search);

    return {
      action: params.get('action') || '',
      code: params.get('code') || '',
      authority: params.get('authority') || '',
      country: params.get('country') || '',
    };
  }, [location.search]);

  const hasDeeplinkParams = useMemo(
    () =>
      !!queryParams.action ||
      !!queryParams.code ||
      !!queryParams.authority ||
      !!queryParams.country,
    [
      queryParams.action,
      queryParams.authority,
      queryParams.code,
      queryParams.country,
    ]
  );

  const deeplinkKey = useMemo(
    () =>
      [
        queryParams.action || '',
        queryParams.code || '',
        queryParams.authority || '',
        queryParams.country || '',
      ].join('|'),
    [
      queryParams.action,
      queryParams.authority,
      queryParams.code,
      queryParams.country,
    ]
  );

  useEffect(() => {
    if (!hasDeeplinkParams) {
      setPersistedDeeplinkKey('');
      return;
    }

    if (persistedDeeplinkKey === deeplinkKey) return;

    dispatch(
      setDeeplinkData({
        action: queryParams.action || null,
        payload_code: queryParams.code || null,
        authority: queryParams.authority || null,
        country: queryParams.country || null,
        needsDeeplinkRedirection: !!queryParams.action,
      })
    );
    setPersistedDeeplinkKey(deeplinkKey);
  }, [
    dispatch,
    deeplinkKey,
    hasDeeplinkParams,
    persistedDeeplinkKey,
    queryParams.action,
    queryParams.authority,
    queryParams.code,
    queryParams.country,
  ]);

  useEffect(() => {
    const title = translate('Route handler') || 'Route handler';
    setHeaderTitle?.(title);

    const hasAuthParams = !!queryParams.authority || !!queryParams.country;

    if (!hasAuthParams) {
      setHasProcessedAuthParams(true);
      setIsProfileSyncing(false);
      return;
    }

    let isCancelled = false;

    const updateProfile = async () => {
      setHasProcessedAuthParams(false);
      setIsProfileSyncing(true);
      try {
        await dispatch(
          changeAndPostProfile({
            authority: queryParams.authority,
            country: queryParams.country,
          })
        );
      } catch (error) {
        console.error('Failed to update profile from route handler', error);
      } finally {
        if (!isCancelled) {
          setIsProfileSyncing(false);
          setHasProcessedAuthParams(true);
        }
      }
    };

    updateProfile();

    return () => {
      isCancelled = true;
    };
  }, [dispatch, queryParams.authority, queryParams.country, setHeaderTitle]);

  const redirectComponent = useMemo(
    () => getRedirectForAction({ ...queryParams, isAuthenticated }),
    [queryParams, isAuthenticated]
  );

  useEffect(() => {
    if (!queryParams.action) return;
    if (!redirectComponent && !fallbackRedirect) return;
  }, [dispatch, queryParams.action, redirectComponent, fallbackRedirect]);

  const [fallbackRedirect, setFallbackRedirect] = useState(false);
  const shouldWaitForProfileSync =
    (!!queryParams.authority || !!queryParams.country) &&
    (!hasProcessedAuthParams || isProfileSyncing);
  const shouldWaitForDeeplinkPersistence =
    hasDeeplinkParams && persistedDeeplinkKey !== deeplinkKey;

  useEffect(() => {
    setFallbackRedirect(false);

    if (redirectComponent) return;

    const timer = setTimeout(() => {
      setFallbackRedirect(true);
    }, FALLBACK_REDIRECT_TIMEOUT_MS);

    return () => clearTimeout(timer);
  }, [queryParams.action, redirectComponent]);

  if (
    redirectComponent &&
    !shouldWaitForProfileSync &&
    !shouldWaitForDeeplinkPersistence
  ) {
    return redirectComponent;
  }

  if (fallbackRedirect) {
    return getFallbackRedirect();
  }

  return (
    <div className="flex flex-1 items-center justify-center">
      <NOVOLoader />
    </div>
  );
};

export default withRouter(RouteHandler);
